import type { ReactNode } from "react";

export type BannerVariant = "promo" | "subtle" | "dark";

export type InlineBannerVariant = "promo" | "soft" | "subtle";

/** An icon visual with no `icon` shows the tag icon. */
export type BannerIconVisual = { type: "icon"; icon?: ReactNode };

export type BannerImageVisual = { type: "image"; src: string; alt: string };

export type BannerVisual = BannerIconVisual | BannerImageVisual;

export type BannerIconTone = "light" | "color" | "dark";

type BannerVariantTokens = {
  background: string;
  bordered: boolean;
  titleColor: string;
  textColor: string;
  accentColor: string;
  closeColor: string;
  iconTone: BannerIconTone;
  /** White CTA on a coloured or dark surface instead of the primary one. */
  invertedCta: boolean;
};

const ON_SURFACE = {
  titleColor: "text.primary",
  textColor: "text.secondary",
  accentColor: "primary.main",
  closeColor: "action.active",
  iconTone: "light",
  invertedCta: false,
} as const;

export const BANNER_VARIANTS: Record<
  BannerVariant | InlineBannerVariant,
  BannerVariantTokens
> = {
  promo: {
    background: "banner.promoBackground",
    bordered: false,
    titleColor: "common.white",
    textColor: "banner.promoSecondaryText",
    accentColor: "common.white",
    closeColor: "common.white",
    iconTone: "color",
    invertedCta: true,
  },
  subtle: {
    ...ON_SURFACE,
    background: "banner.subtleBackground",
    bordered: true,
  },
  soft: {
    ...ON_SURFACE,
    background: "primary.selected",
    bordered: false,
  },
  dark: {
    background: "banner.darkBackground",
    bordered: true,
    titleColor: "common.white",
    textColor: "banner.darkSecondaryText",
    accentColor: "common.white",
    closeColor: "common.white",
    iconTone: "dark",
    invertedCta: true,
  },
};

type BannerSurfaceOptions = {
  /** The surface is an elevated Paper (sticky bar, slide-in). */
  paper?: boolean;
  /** Inline draws no border in any variant. */
  borderless?: boolean;
};

/**
 * A banner's fill and border. On a Paper, Subtle keeps the Paper's own fill,
 * which dark mode lifts with an overlay; every other surface paints its
 * variant's colour and drops that overlay.
 */
export const bannerSurfaceSx = (
  variant: BannerVariant | InlineBannerVariant,
  { paper = false, borderless = false }: BannerSurfaceOptions = {}
) => {
  const tokens = BANNER_VARIANTS[variant];

  return {
    borderRadius: 2,
    ...(tokens.bordered &&
      !borderless && { border: 1, borderColor: "divider" }),
    ...(!(paper && variant === "subtle") && {
      bgcolor: tokens.background,
      backgroundImage: "none",
    }),
  };
};

/** Figma's floating-banner shadows; the theme has no matching elevation. */
export const STICKY_BAR_SHADOW =
  "0px 2px 3px rgba(0, 0, 0, 0.08), 0px 8px 12px rgba(0, 0, 0, 0.16)";

export const SLIDE_IN_SHADOW =
  "0px 2px 6px rgba(0, 0, 0, 0.08), 0px 8px 24px -4px rgba(0, 0, 0, 0.16)";

/** Figma's inset shade on the countdown's boxes, on the Promo and Dark styles. */
export const COUNTDOWN_INSET_SHADOW = "inset 0px 2px 11px rgba(0, 0, 0, 0.2)";
